import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PLANNED_OUTREACHES } from '../data/homeOutreaches';
import { getPublishedEvents } from '../lib/api/events';
import { captureError } from '../lib/capture-error';
import { formatLocalDate, parseLocalDate } from '../lib/dates';
import { buildEventApplyPath } from '../lib/signup-options';
import type { Event } from '../types/database';
import './HomeOutreachesPanel.css';

function formatLocation(event: Event): string | null {
  if (event.location_city && event.location_state?.trim()) {
    return `${event.location_city}, ${event.location_state.trim()}`;
  }
  const parts = [event.location_city, event.location_state?.trim(), event.location_country]
    .filter(Boolean);
  return parts[0] || null;
}

function formatWhen(event: Event): string {
  return formatLocalDate(event.start_date, { month: 'long', year: 'numeric' });
}

function shortSummary(event: Event): string {
  const raw = (event.description || '').trim();
  if (!raw) return '';
  // Prefer the first sentence; avoid dangling "includes:" list intros
  const firstSentence = raw.split(/(?<=\.)\s+/)[0]?.trim() || raw.split(/\n/)[0].trim();
  const cleaned = firstSentence.replace(/\s*This event will include:?\s*$/i, '').trim();
  if (cleaned.length <= 160) return cleaned;
  return `${cleaned.slice(0, 157).trimEnd()}…`;
}

function isStillOpen(event: Event, now: Date): boolean {
  const end = parseLocalDate(event.end_date || event.start_date);
  // Keep visible through the end calendar day
  const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
  return endOfDay >= now;
}

/** Direct apply is only valid for published standalone (non-parent, non-legacy) events. */
function canApplyDirectly(event: Event): boolean {
  return event.status === 'published' && !event.is_parent_event && !event.is_legacy_event;
}

const HomeOutreachesPanel: React.FC = () => {
  const [openEvents, setOpenEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(false);
        const published = await getPublishedEvents();
        const now = new Date();
        const open = published
          // getPublishedEvents also returns draft parents as catalog "coming soon"
          .filter((event) => event.status === 'published' && isStillOpen(event, now))
          .sort(
            (a, b) =>
              parseLocalDate(a.start_date).getTime() - parseLocalDate(b.start_date).getTime()
          );
        if (!cancelled) {
          setOpenEvents(open);
          setLoadError(false);
        }
      } catch (err) {
        captureError(err, 'Failed to load homepage outreaches');
        if (!cancelled) {
          setOpenEvents([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const featured = openEvents[0];
  const showApply = featured ? canApplyDirectly(featured) : false;

  return (
    <section className="home-outreaches" aria-labelledby="home-outreaches-heading">
      <div className="container">
        <div className="home-outreaches-panel">
          {loading ? (
            <div className="home-outreach-featured-body text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading outreaches…</span>
              </div>
            </div>
          ) : loadError ? (
            <div className="home-outreach-featured-body">
              <h2 id="home-outreaches-heading" className="home-outreach-featured-title">
                Join an Outreach
              </h2>
              <p className="home-outreaches-empty mb-0" role="alert">
                We couldn&apos;t load open events right now. Please try again, or browse{' '}
                <Link to="/events">all events</Link>.
              </p>
            </div>
          ) : featured ? (
            <div className="home-outreach-featured">
              {featured.featured_image_url && (
                <img
                  src={featured.featured_image_url}
                  alt=""
                  className="home-outreach-featured-image"
                />
              )}
              <div className="home-outreach-featured-body">
                <p className="home-outreach-kicker">Open for signup</p>
                <h2 id="home-outreaches-heading" className="home-outreach-featured-title">
                  {featured.title}
                </h2>
                <p className="home-outreach-featured-meta">
                  {formatWhen(featured)}
                  {formatLocation(featured) ? ` · ${formatLocation(featured)}` : ''}
                </p>
                {shortSummary(featured) && (
                  <p className="home-outreach-featured-summary">{shortSummary(featured)}</p>
                )}
                <div className="home-outreach-actions">
                  <Link
                    to={`/events/${featured.slug}`}
                    className={`btn btn-lg px-4 ${showApply ? 'btn-outline-primary' : 'btn-primary'}`}
                  >
                    {featured.is_parent_event ? 'View sites & register' : 'Learn more'}
                  </Link>
                  {showApply && (
                    <Link
                      to={buildEventApplyPath(featured.slug)}
                      className="btn btn-primary btn-lg px-4"
                    >
                      <i className="fas fa-clipboard-check me-2" aria-hidden />
                      Apply
                    </Link>
                  )}
                  {openEvents.length > 1 && (
                    <Link to="/events" className="btn btn-link">
                      All open events
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="home-outreach-featured-body">
              <h2 id="home-outreaches-heading" className="home-outreach-featured-title">
                Join an Outreach
              </h2>
              <p className="home-outreaches-empty mb-0">
                No events are open for signup right now. Check back soon, or browse{' '}
                <Link to="/events">all events</Link>.
              </p>
            </div>
          )}

          {PLANNED_OUTREACHES.length > 0 && (
            <div className="home-outreaches-upcoming">
              <h3 className="home-outreaches-upcoming-heading">Future Outreaches</h3>
              <ul className="home-outreaches-upcoming-list list-unstyled mb-0">
                {PLANNED_OUTREACHES.map((event) => (
                  <li key={event.id} className="home-outreach-upcoming-item">
                    <div className="home-outreach-upcoming-copy">
                      <span className="home-outreach-upcoming-title">{event.title}</span>
                      <span className="home-outreach-upcoming-location">
                        Tentative: {event.location}
                      </span>
                    </div>
                    <time className="home-outreach-upcoming-when">{event.when}</time>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeOutreachesPanel;
